import { useState, type KeyboardEvent } from "react";
import { ChatIcon } from "../ui/icons/Icon";

// Piezas de formulario del kit compartidas por Configuración de la empresa, Usuarios y la cuenta propia (Mi perfil,
// Preferencias, Seguridad): tarjetas de opción C08 con radio y campo C05 con su error y ayuda debajo.

/** Tarjetas de opción C08 (radio): Tab entra en la elegida y las flechas mueven y eligen. */
export function OptionCards({
  labelledBy,
  name,
  value,
  options,
  onChange,
}: {
  labelledBy: string;
  name: string;
  value: string;
  options: { value: string; title: string; line: string }[];
  onChange: (value: string) => void;
}) {
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + step + options.length) % options.length]!.value;
    onChange(next);
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>(`[data-option="${name}-${next}"]`)?.focus({ preventScroll: true }),
    );
  };
  return (
    <div className="tm-options tm-mode-cards" role="radiogroup" aria-labelledby={labelledBy} onKeyDown={move}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="tm-option"
          role="radio"
          data-option={`${name}-${option.value}`}
          aria-checked={value === option.value ? "true" : "false"}
          tabIndex={value === option.value ? 0 : -1}
          onClick={() => onChange(option.value)}
        >
          <span className="tm-radio" aria-hidden="true" />
          <span className="tm-option-text">
            <strong>{option.title}</strong>
            <small>{option.line}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Campo C05: etiqueta arriba, «(opcional)» en gris, ayuda y error debajo del campo. Una contraseña lleva «Mostrar» dentro
 * del campo (el valor no se borra al alternar) y deja pegar y usar el gestor de contraseñas.
 */
export function TextField({
  id,
  field,
  label,
  optional,
  help,
  type = "text",
  value,
  error,
  maxLength,
  autoComplete,
  placeholder,
  showLabel,
  onChange,
}: {
  id: string;
  field?: string;
  label: string;
  optional?: string;
  help?: string;
  type?: string;
  value: string;
  error?: string;
  maxLength: number;
  autoComplete: string;
  placeholder?: string;
  /** Con contraseña: nombre del botón que la muestra («Mostrar contraseña»). */
  showLabel?: string;
  onChange: (value: string) => void;
}) {
  const [shown, setShown] = useState(false);
  const described = [error ? `${id}-error` : null, help ? `${id}-help` : null].filter(Boolean).join(" ");
  const secret = type === "password" && !!showLabel;
  const input = (
    <input
      id={id}
      type={secret && shown ? "text" : type}
      value={value}
      maxLength={maxLength}
      autoComplete={autoComplete}
      placeholder={placeholder}
      spellCheck={false}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={described || undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
  return (
    <div className={`tm-field ${error ? "invalid" : ""}`} data-field={field}>
      <label htmlFor={id}>
        {label}
        {optional ? <span className="tm-hint"> {optional}</span> : null}
      </label>
      {secret ? (
        <span className="tm-secret">
          {input}
          <button
            type="button"
            className={`tm-icon-btn tm-secret-toggle${shown ? " on" : ""}`}
            aria-label={showLabel}
            title={showLabel}
            aria-pressed={shown ? "true" : "false"}
            aria-controls={id}
            onClick={() => setShown(!shown)}
          >
            <ChatIcon name="eye" />
          </button>
        </span>
      ) : (
        input
      )}
      {error ? (
        <span id={`${id}-error`} className="tm-field-error">
          {error}
        </span>
      ) : null}
      {help ? (
        <small id={`${id}-help`} className="tm-field-help">
          {help}
        </small>
      ) : null}
    </div>
  );
}
